import React, { FC, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { Box, Heading } from '@moonpig/launchpad-components'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { CollectionTile } from './CollectionTile'
import type { CollectionTileData } from './types'
import { createViewListEvent, createSelectItemEvent } from './tracking'

const StyledCollectionSection = styled(Box)`
  width: 100%;
  ${s({
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 2, lg: 4 },
    py: { xs: 4, md: 0 },
  })}
`
const StyledScrollRegion = styled(Box)`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  ${breakpoint('lg')} {
    overflow-x: visible;
    overflow-y: visible;
  }
`

const StyledTileGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, minmax(280px, 1fr));
  align-items: stretch;
  justify-items: stretch;
  width: max-content;
  ${({ theme }) =>
    s({
      gap: { xs: '8px' },
      p: { xs: 6, lg: 0 },
      maxWidth: { xs: '100%', lg: theme.siteWidth },
    })}
  ${breakpoint('lg')} {
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

type Props = {
  tiles: CollectionTileData[]
  pageLocation: string
}

export const CollectionTilesModule: FC<Props> = ({ tiles, pageLocation }) => {
  const handleClick = (tile: CollectionTileData) => {
    const index = tiles.indexOf(tile)
    trackGAEvent(
      createSelectItemEvent({
        label: `${pageLocation} | ${tile.title}`.toLowerCase(),
        action: 'select collection',
        category: 'collections',
        tile,
        itemBrand: 'moonpig',
        mission: tile.mission,
        index,
      }),
    )
  }

  const [canTrackRef, canTrack] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })

  useEffect(() => {
    if (canTrack && tiles.length) {
      const label = `${pageLocation} | collections | grid`
      trackGAEvent(createViewListEvent(tiles, label, 'moonpig'))
    }
  }, [canTrack, tiles, pageLocation])

  return (
    <StyledCollectionSection ref={canTrackRef} data-testid="collection-tiles">
      <Box width={1} textAlign="left" pl={{ xs: 6, lg: 0 }}>
        <Heading
          level="h2"
          my={0}
          typography={{
            xs: 'typeMobileDisplay03',
            md: 'typeDesktopDisplay04',
          }}
        >
          Pair the perfect card &amp; gift
        </Heading>
      </Box>
      <StyledScrollRegion>
        <StyledTileGrid>
          {tiles.map(tile => (
            <CollectionTile
              key={tile.title}
              tile={tile}
              onClick={() => handleClick(tile)}
            />
          ))}
        </StyledTileGrid>
      </StyledScrollRegion>
    </StyledCollectionSection>
  )
}
